import { useEffect } from 'react';

import type { QuestionItem, QuestionObjective, QuestionProduct, QuestionTheme, QuestionTree } from 'pages/custom_tests/components/question_picker/QuestionPicker';
import { TestSummaryBar } from 'pages/custom_tests/components/custom_test_wizard/components/test_summary_bar/TestSummaryBar';
import { QuestionPreviewModal } from 'pages/custom_tests/components/question_preview/QuestionPreviewModal';
import type { PreviewQuestion } from 'pages/custom_tests/components/question_preview/QuestionPreviewModal';
import { Alert, Badge, Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { usePreviewStudent } from 'pages/custom_tests/hooks/usePreviewStudent';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

type QuestionSelectorProps = {
    focusObjectiveId?: number;
    loading: boolean;
    minutes?: number;
    onChange: (questionIds: string, totalSelected: number) => void;
    onFocusHandled?: () => void;
    questionTree: QuestionTree;
    richSummary?: boolean;
    selectedIds?: string;
    showActualTime?: boolean;
};

const INITIAL_TOPIC_COUNT = 7;
const SOURCE_FILTER_OPTIONS = ['Practice Test', 'Course', 'Quiz'] as const;

type SourceFilterOption = typeof SOURCE_FILTER_OPTIONS[number];

type SortColumn = 'name' | 'qid' | 'source' | 'type';

const themeKey = (product: QuestionProduct, theme: QuestionTheme) => `${product.ProductId}-${theme.ThemeId}`;

// Application questions are identified by their resolved type name (numeric type id '1'
// as a fallback) — never by substring-matching the numeric QuestionType field.
const isApplicationQuestion = (question: QuestionItem): boolean => question.QuestionTypeName === 'Application' || question.QuestionType === '1';

const themeQuestions = (theme: QuestionTheme): QuestionItem[] => (theme.Objectives ?? []).flatMap(objective => objective.Questions ?? []);

// Prefer the resolved display name for a question's type, falling back to the numeric id
// only when no name was provided by the source XML.
const getQuestionTypeName = (question: QuestionItem): string => {

    const source = question as any;
    const options = [
        question.QuestionTypeName,
        source.QuestionTypeName,
        source.TypeName,
        question.QuestionType,
        source.Type
    ];

    return options.find(value => typeof value === 'string' && value.trim().length > 0)?.trim() ?? '';

};

const splitSourceString = (value: string): string[] => value
    .split(/\s*(?:\||;|,|•)\s*/g)
    .map(item => item.trim())
    .filter(Boolean);

const classifySource = (value: string): SourceFilterOption | undefined => {

    const normalized = value.trim().toLowerCase();

    if (normalized.includes('practice') && normalized.includes('test')) return 'Practice Test';
    if (normalized.includes('course')) return 'Course';
    if (normalized.includes('quiz')) return 'Quiz';

    return undefined;

};

const getQuestionSourceOptions = (question: QuestionItem): Set<SourceFilterOption> => {

    const options = new Set<SourceFilterOption>();
    const source = question as any;

    const tokens = [
        ...(typeof source.QuestionSource === 'string' ? splitSourceString(source.QuestionSource) : []),
        ...(typeof question.Source === 'string' ? splitSourceString(question.Source) : []),
        ...(typeof source.SourceName === 'string' ? splitSourceString(source.SourceName) : []),
        ...(typeof source.SourceType === 'string' ? splitSourceString(source.SourceType) : []),
        ...(Array.isArray(source.Sources) ? source.Sources.map(item => String(item)) : [])
    ];

    tokens.forEach(token => {

        const sourceOption = classifySource(token);
        if (sourceOption) options.add(sourceOption);

    });

    return options;

};

// Seeds the selected-question set STRICTLY from the cached id list so navigating back into
// this step restores the previous selection instead of clearing it. The tree's server-stamped
// IsChecked flags are deliberately ignored — unioning them back in would silently re-select
// questions the user deselected when the step remounts against a cached tree.
const computeSeededSelection = (tree: QuestionTree, ids?: string): Set<number> => {

    const seed = new Set((ids ?? '').split(',').map(s => s.trim()).filter(Boolean).map(Number));
    const result = new Set<number>();

    (tree.Products ?? []).forEach(product => (product.Themes ?? []).forEach(theme => (theme.Objectives ?? []).forEach(objective => (objective.Questions ?? []).forEach(question => {

        if (seed.has(question.QuestionId)) result.add(question.QuestionId);

    }))));

    return result;

};

export const QuestionSelector = (props: QuestionSelectorProps) => {

    const { focusObjectiveId, loading, minutes, onChange, onFocusHandled, questionTree, richSummary = false, selectedIds, showActualTime } = props;

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();
    const { cultureId, userId } = useApp();

    const { resolvePreviewStudentId } = usePreviewStudent();

    const [state, setState] = useSuperState({
        applicationWarningDismissed: false,
        expandedThemes: new Set<string>(),
        highlightObjectiveId: 0,
        objectiveFilter: new Set<number>(),
        objectiveOpen: false,
        objectiveSearch: '',
        previewIndex: 0,
        previewQuestions: [] as PreviewQuestion[],
        previewShow: false,
        previewStudentId: 0,
        selectedMode: 'all' as 'all' | 'selected' | 'unselected',
        selectedOpen: false,
        selectedQuestionIds: computeSeededSelection(questionTree, selectedIds),
        showAllObjectives: false,
        showAllTopics: false,
        sortColumn: '' as SortColumn | '',
        sortDir: 'asc' as 'asc' | 'desc',
        sourceFilter: new Set<SourceFilterOption>(),
        sourceOpen: false,
        sourceSearch: '',
        topicFilter: new Set<number>(),
        topicOpen: true,
        topicSearch: '',
        typeFilter: new Set<string>(),
        typeOpen: false,
        typeSearch: ''
    });

    const { applicationWarningDismissed, expandedThemes, highlightObjectiveId, objectiveFilter, objectiveOpen, objectiveSearch, previewIndex, previewQuestions, previewShow,
        previewStudentId, selectedMode, selectedOpen, selectedQuestionIds, showAllObjectives, showAllTopics, sortColumn, sortDir, sourceFilter, sourceOpen, sourceSearch,
        topicFilter, topicOpen, topicSearch, typeFilter, typeOpen, typeSearch } = state;

    // Opens the mock's Question Preview modal, which renders a question by its QuestionId in
    // "in-app" mode. The student site's viewer authorizes teacher previews through a real
    // teacher -> student relationship, so a partner student is resolved first (an empty
    // student id fails to load). The list is the objective's filtered/sorted question list so
    // the modal's Previous/Next steps through the same rows the table shows.
    const openQuestionPreview = async (questions: QuestionItem[], question: QuestionItem, objectiveName: string, topicName: string) => {

        const resolvedStudentId = await resolvePreviewStudentId();

        if (!resolvedStudentId) {

            createToast(
                formatMessage({ id: 'question_preview_requires_at_least_one_a.229fb122', defaultMessage: 'Question preview requires at least one active student in the current partner.' }),
                'warning',
                6000
            );
            return;

        }

        const nextPreviewQuestions: PreviewQuestion[] = questions.map(q => ({
            Name: q.Name,
            ObjectiveName: objectiveName,
            QuestionId: q.QuestionId,
            Sources: q.Source ?? '',
            TopicName: topicName,
            TypeName: getQuestionTypeName(q)
        }));

        setState({
            previewIndex: Math.max(0, nextPreviewQuestions.findIndex(q => q.QuestionId === question.QuestionId)),
            previewQuestions: nextPreviewQuestions,
            previewShow: true,
            previewStudentId: resolvedStudentId
        });

    };

    const products = questionTree.Products ?? [];

    const allThemes = products.flatMap(product => (product.Themes ?? []).map(theme => ({ product, theme })));
    const allObjectives = allThemes.flatMap(({ theme }) => theme.Objectives ?? []);
    const allQuestions = allObjectives.flatMap(objective => objective.Questions ?? []);
    const allTypes = Array.from(new Set(allQuestions.map(getQuestionTypeName).filter(Boolean)));
    const uniqueObjectives = Array.from(new Map(allObjectives.map(objective => [objective.ObjectiveId, objective])).values());

    const matchesSearch = (value: string, term: string) => !term.trim() || value.toLowerCase().includes(term.trim().toLowerCase());

    const filteredObjectives = uniqueObjectives.filter(objective => matchesSearch(objective.Name, objectiveSearch));
    const visibleObjectives = showAllObjectives ? filteredObjectives : filteredObjectives.slice(0, INITIAL_TOPIC_COUNT);
    const filteredTypes = allTypes.filter(type => matchesSearch(type, typeSearch));
    const filteredSources = SOURCE_FILTER_OPTIONS.filter(source => matchesSearch(source, sourceSearch));

    // --- Selection sync ---
    // Reseed only when a new question tree loads, reading the cached selection (selectedIds)
    // at that moment. selectedIds is intentionally omitted from the deps so a user's in-step
    // toggles do not re-trigger a reseed (which would fight their edits).
    useEffect(() => {

        setState({ selectedQuestionIds: computeSeededSelection(questionTree, selectedIds) });

    }, [questionTree]);

    useEffect(() => {

        // Wait until the question tree has loaded before reporting the selection upward.
        // Firing on mount with an empty tree would push an empty selection to the wizard
        // and wipe the saved question ids before they can be seeded (breaks edit-on-refresh).
        if (!(questionTree.Products?.length)) return;

        onChange(Array.from(selectedQuestionIds).join(','), selectedQuestionIds.size);

    }, [selectedQuestionIds]);

    // Deep-link from the Summary step: expand the target objective's theme and scroll it into
    // view. Re-runs once the theme has expanded (expandedThemes dep) so the row exists in the DOM.
    useEffect(() => {

        // Wait until the tree is loaded before acting, otherwise the focus would be cleared
        // before the objective can be found.
        if (!focusObjectiveId || !products.length) return;

        let targetThemeKey = '';
        products.forEach(product => (product.Themes ?? []).forEach(theme => {

            if ((theme.Objectives ?? []).some(o => o.ObjectiveId === focusObjectiveId)) targetThemeKey = themeKey(product, theme);

        }));

        if (targetThemeKey && !expandedThemes.has(targetThemeKey)) {

            setState(prev => {

                const next = new Set(prev.expandedThemes);
                next.add(targetThemeKey);
                return { expandedThemes: next };

            });
            return;

        }

        if (targetThemeKey) {

            document.getElementById(`objective-block-${focusObjectiveId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setState({ highlightObjectiveId: focusObjectiveId });

        }

        onFocusHandled?.();

    }, [focusObjectiveId, expandedThemes, products]);

    // Fade the focus highlight shortly after the objective is brought into view.
    useEffect(() => {

        if (!highlightObjectiveId) return undefined;

        const timer = setTimeout(() => setState({ highlightObjectiveId: 0 }), 2000);
        return () => clearTimeout(timer);

    }, [highlightObjectiveId]);

    // --- Filtering ---
    const questionMatchesFilters = (question: QuestionItem): boolean => {

        if (typeFilter.size && !typeFilter.has(getQuestionTypeName(question))) return false;
        if (sourceFilter.size) {

            const questionSources = getQuestionSourceOptions(question);
            const hasSelectedSource = Array.from(sourceFilter).some(source => questionSources.has(source));
            if (!hasSelectedSource) return false;

        }
        if (selectedMode === 'selected' && !selectedQuestionIds.has(question.QuestionId)) return false;
        if (selectedMode === 'unselected' && selectedQuestionIds.has(question.QuestionId)) return false;
        return true;

    };

    const visibleProducts = products.map(product => ({
        ...product,
        Themes: (product.Themes ?? [])
            .filter(theme => !topicFilter.size || topicFilter.has(theme.ThemeId))
            .map(theme => ({
                ...theme,
                Objectives: (theme.Objectives ?? [])
                    .filter(objective => !objectiveFilter.size || objectiveFilter.has(objective.ObjectiveId))
                    .map(objective => ({ ...objective, Questions: (objective.Questions ?? []).filter(questionMatchesFilters) }))
                    .filter(objective => objective.Questions.length > 0)
            }))
            .filter(theme => theme.Objectives.length > 0)
    })).filter(product => product.Themes.length > 0);

    // --- Selection helpers ---
    const setSelection = (updater: (draft: Set<number>) => void) => {

        const draft = new Set(selectedQuestionIds);
        updater(draft);
        setState({ selectedQuestionIds: draft });

    };

    const toggleQuestion = (id: number) => setSelection(draft => (draft.has(id) ? draft.delete(id) : draft.add(id)));

    const setThemeSelection = (theme: QuestionTheme, select: boolean) => setSelection(draft => themeQuestions(theme).forEach(q => (select ? draft.add(q.QuestionId) : draft.delete(q.QuestionId))));

    const setObjectiveSelection = (objective: QuestionObjective, select: boolean) => setSelection(draft => (objective.Questions ?? []).forEach(q => (select ? draft.add(q.QuestionId) : draft.delete(q.QuestionId))));

    const selectedCountIn = (questions: QuestionItem[]) => questions.filter(q => selectedQuestionIds.has(q.QuestionId)).length;

    // --- Sidebar toggle helpers ---
    const toggleSetValue = <T, >(set: Set<T>, value: T): Set<T> => {

        const next = new Set(set);
        if (next.has(value)) next.delete(value); else next.add(value);
        return next;

    };

    const toggleExpanded = (key: string) => setState({ expandedThemes: toggleSetValue(expandedThemes, key) });

    // --- Sorting ---
    // One shared sort descriptor applies to every objective's table; clicking a header cycles
    // that column asc -> desc.
    const cycleSort = (column: SortColumn) => setState({
        sortColumn: column,
        sortDir: sortColumn === column && sortDir === 'asc' ? 'desc' : 'asc'
    });

    const sortQuestions = (questions: QuestionItem[]): QuestionItem[] => {

        if (!sortColumn) return questions;

        const direction = sortDir === 'asc' ? 1 : -1;

        const sortValue = (question: QuestionItem): number | string => {

            if (sortColumn === 'qid') return question.QuestionId;
            if (sortColumn === 'name') return question.Name ?? '';
            if (sortColumn === 'type') return getQuestionTypeName(question);
            return question.Source ?? '';

        };

        return [...questions].sort((a, b) => {

            const left = sortValue(a);
            const right = sortValue(b);

            if (typeof left === 'number' && typeof right === 'number') return (left - right) * direction;

            return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) * direction;

        });

    };

    const renderSortHeader = (column: SortColumn, label: string) => (
        <button
            className={'btn btn-link btn-sm p-0 text-decoration-none text-muted fw-normal'}
            onClick={() => cycleSort(column)}
            type={'button'}
        >
            {label}
            <i className={`ms-1 fa-solid ${sortColumn === column ? (sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`} />
        </button>
    );

    // Per-source question counts for the Source filter checkboxes, computed from the loaded tree.
    const sourceCounts = new Map<SourceFilterOption, number>();
    allQuestions.forEach(question => getQuestionSourceOptions(question).forEach(option => sourceCounts.set(option, (sourceCounts.get(option) ?? 0) + 1)));

    // --- Summary ---
    // SMS requirement notice (unique selected questions): all-application tests need SMS;
    // mixing application and knowledge questions needs SMS 8.
    const selectedQuestionList = Array.from(
        new Map(allQuestions.filter(q => selectedQuestionIds.has(q.QuestionId)).map(q => [q.QuestionId, q])).values()
    );
    const hasApplication = selectedQuestionList.some(isApplicationQuestion);
    const hasNonApplication = selectedQuestionList.some(q => !isApplicationQuestion(q));

    // Reset the dismissal once no application questions remain selected, so the warning can
    // reappear the next time an application question is added.
    useEffect(() => {

        if (!hasApplication && state.applicationWarningDismissed) setState({ applicationWarningDismissed: false });

    }, [hasApplication]);

    // --- Sidebar topic list ---
    const topicItems = allThemes
        .filter(({ theme }) => !topicSearch.trim() || theme.Name.toLowerCase().includes(topicSearch.trim().toLowerCase()));
    const visibleTopicItems = showAllTopics ? topicItems : topicItems.slice(0, INITIAL_TOPIC_COUNT);

    // Checkbox rows kept in a flex layout so the box aligns to the first line of (possibly
    // wrapping) label text instead of drifting out of line.
    const renderFilterCheck = (id: string, label: string, checked: boolean, onToggle: () => void) => (
        <label className={'d-flex align-items-start gap-2 mb-1'} htmlFor={id} key={id} role={'button'}>
            <input
                checked={checked}
                className={'form-check-input mt-1 flex-shrink-0'}
                id={id}
                onChange={onToggle}
                type={'checkbox'}
            />
            <span className={'small'}>
                {label}
            </span>
        </label>
    );

    const renderSearch = (value: string, onChange: (next: string) => void) => (
        <Form.Control
            className={'mb-2'}
            onChange={e => onChange(e.target.value)}
            placeholder={formatMessage({ id: 'search.0ab06b2c', defaultMessage: 'Search' })}
            size={'sm'}
            value={value}
        />
    );

    const SectionHeader = (headerProps: { label: string; onToggle: () => void; open: boolean }) => (
        <button
            className={'btn btn-link text-decoration-none d-flex justify-content-between align-items-center w-100 px-0 text-dark fw-bold'}
            onClick={headerProps.onToggle}
            type={'button'}
        >
            <span className={'small text-uppercase text-muted'}>
                {headerProps.label}
            </span>
            <i className={`fa-solid ${headerProps.open ? 'fa-minus' : 'fa-plus'} text-muted`} />
        </button>
    );

    if (loading) {

        return (
            <div className={'d-flex align-items-center justify-content-center p-5'}>
                <GMetrixLoader size={'large'} />
            </div>
        );

    }

    return (
        <div className={'question-selector d-flex flex-column gap-3 py-2'}>

            <TestSummaryBar minutes={minutes} questionTree={questionTree} richSummary={richSummary} selectedQuestionIds={selectedQuestionIds} showActualTime={showActualTime} />

            {hasApplication && !applicationWarningDismissed && (
                <Alert
                    dismissible
                    className={'mb-0'}
                    onClose={() => setState({ applicationWarningDismissed: true })}
                    variant={'warning'}
                >
                    <div className={'d-flex align-items-start gap-2'}>
                        <i className={'fa-solid fa-circle-exclamation text-warning mt-1'} />
                        <div>
                            <div className={'fw-bold'}>
                                {hasNonApplication
                                    ? (formatMessage({ id: 'sms_8_required.fd3a8ae8', defaultMessage: 'SMS 8 required' }))
                                    : (formatMessage({ id: 'sms_required.e1197bd8', defaultMessage: 'SMS required' }))}
                            </div>
                            <div className={'small'}>
                                {hasNonApplication
                                    ? (formatMessage({ id: 'mixing_application_and_knowledge_questio.18009628', defaultMessage: 'Mixing Application and knowledge questions requires SMS 8.' }))
                                    : (formatMessage({ id: 'application_questions_require_the_skills.bfa8083e', defaultMessage: 'Application questions require the Skills Management System (SMS).' }))}
                            </div>
                        </div>
                    </div>
                </Alert>
            )}

            <Row className={'g-3'}>

                <Col lg={3} md={4} xs={12}>
                    <div className={'d-flex flex-column gap-2'}>

                        <div className={'border rounded p-3'}>
                            <SectionHeader label={formatMessage({ id: 'topic.671ec7b1', defaultMessage: 'Topic' })} onToggle={() => setState({ topicOpen: !topicOpen })} open={topicOpen} />
                            {topicOpen && (
                                <div className={'mt-2 d-flex flex-column'}>
                                    {renderSearch(topicSearch, next => setState({ topicSearch: next }))}
                                    <div className={'d-flex flex-column'}>
                                        {visibleTopicItems.map(({ product, theme }) => renderFilterCheck(
                                            `topic-${themeKey(product, theme)}`,
                                            `${theme.Name} (${themeQuestions(theme).length})`,
                                            topicFilter.has(theme.ThemeId),
                                            () => setState({ topicFilter: toggleSetValue(topicFilter, theme.ThemeId) })
                                        ))}
                                    </div>
                                    {topicItems.length > INITIAL_TOPIC_COUNT && (
                                        <button
                                            className={'btn btn-link btn-sm text-decoration-none px-0 align-self-start'}
                                            onClick={() => setState({ showAllTopics: !showAllTopics })}
                                            type={'button'}
                                        >
                                            {showAllTopics ?
                                                (
                                                    <FormattedMessage defaultMessage="SEE LESS" id="see_less.2b005529" />
                                                )
                                                :
                                                (
                                                    <>
                                                        <FormattedMessage defaultMessage="SEE MORE" id="see_more.d454d77c" />
                                                        <i className={'fa-solid fa-plus ms-1'} />
                                                    </>
                                                )}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={'border rounded p-3'}>
                            <SectionHeader label={formatMessage({ id: 'objective.4eee91e9', defaultMessage: 'Objective' })} onToggle={() => setState({ objectiveOpen: !objectiveOpen })} open={objectiveOpen} />
                            {objectiveOpen && (
                                <div className={'mt-2 d-flex flex-column'}>
                                    {renderSearch(objectiveSearch, next => setState({ objectiveSearch: next }))}
                                    <div className={'d-flex flex-column'}>
                                        {visibleObjectives.map(objective => renderFilterCheck(
                                            `objective-filter-${objective.ObjectiveId}`,
                                            objective.Name,
                                            objectiveFilter.has(objective.ObjectiveId),
                                            () => setState({ objectiveFilter: toggleSetValue(objectiveFilter, objective.ObjectiveId) })
                                        ))}
                                    </div>
                                    {filteredObjectives.length > INITIAL_TOPIC_COUNT && (
                                        <button
                                            className={'btn btn-link btn-sm text-decoration-none px-0 align-self-start'}
                                            onClick={() => setState({ showAllObjectives: !showAllObjectives })}
                                            type={'button'}
                                        >
                                            {showAllObjectives ?
                                                (
                                                    <FormattedMessage defaultMessage="SEE LESS" id="see_less.2b005529" />
                                                )
                                                :
                                                (
                                                    <>
                                                        <FormattedMessage defaultMessage="SEE MORE" id="see_more.d454d77c" />
                                                        <i className={'fa-solid fa-plus ms-1'} />
                                                    </>
                                                )}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={'border rounded p-3'}>
                            <SectionHeader label={formatMessage({ id: 'question_type.4b121765', defaultMessage: 'Question Type' })} onToggle={() => setState({ typeOpen: !typeOpen })} open={typeOpen} />
                            {typeOpen && (
                                <div className={'mt-2 d-flex flex-column'}>
                                    <div className={'d-flex flex-wrap gap-2'}>
                                        {filteredTypes.length ? filteredTypes.map(type => (
                                            <button
                                                className={`btn btn-sm ${typeFilter.has(type) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                key={type}
                                                onClick={() => setState({ typeFilter: toggleSetValue(typeFilter, type) })}
                                                type={'button'}
                                            >
                                                {type}
                                            </button>
                                        )) : (
                                            <span className={'small text-muted'}>
                                                <FormattedMessage defaultMessage="No data available." id="no_data_available.c5fd1c01" />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={'border rounded p-3'}>
                            <SectionHeader label={formatMessage({ id: 'source.f63dd3d7', defaultMessage: 'Source' })} onToggle={() => setState({ sourceOpen: !sourceOpen })} open={sourceOpen} />
                            {sourceOpen && (
                                <div className={'mt-2 d-flex flex-column'}>
                                    <div className={'d-flex flex-column'}>
                                        {filteredSources.length ? filteredSources.map(source => renderFilterCheck(
                                            `source-filter-${source}`,
                                            `${source} (${sourceCounts.get(source) ?? 0})`,
                                            sourceFilter.has(source),
                                            () => setState({ sourceFilter: toggleSetValue(sourceFilter, source) })
                                        )) : (
                                            <span className={'small text-muted'}>
                                                <FormattedMessage defaultMessage="No data available." id="no_data_available.c5fd1c01" />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={'border rounded p-3'}>
                            <SectionHeader label={formatMessage({ id: 'selected.2c7e9011', defaultMessage: 'Selected' })} onToggle={() => setState({ selectedOpen: !selectedOpen })} open={selectedOpen} />
                            {selectedOpen && (
                                <div className={'mt-2 d-flex flex-wrap gap-2'}>
                                    {(['selected', 'unselected'] as const).map(mode => (
                                        <button
                                            className={`btn btn-sm ${selectedMode === mode ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            key={mode}
                                            onClick={() => setState({ selectedMode: selectedMode === mode ? 'all' : mode })}
                                            type={'button'}
                                        >
                                            {mode === 'selected'
                                                ? (formatMessage({ id: 'selected.2c7e9011', defaultMessage: 'Selected' }))
                                                : (formatMessage({ id: 'unselected.5e1c3bd3', defaultMessage: 'Unselected' }))}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                </Col>

                <Col lg={9} md={8} xs={12}>
                    {visibleProducts.length === 0 ?
                        (
                            <div className={'d-flex justify-content-center align-items-center text-muted border rounded p-5'}>
                                <FormattedMessage defaultMessage="No questions match the selected filters." id="no_questions_match_the_selected_filters.c3821529" />
                            </div>
                        )
                        :
                        (
                            <div className={'d-flex flex-column gap-4'}>
                                {visibleProducts.map(product => (
                                    <div key={product.ProductId}>
                                        <h5 className={'fw-bold mb-2'}>
                                            {product.ProductName}
                                        </h5>

                                        <div className={'d-flex flex-column gap-2'}>
                                            {product.Themes.map(theme => {

                                                const key = themeKey(product, theme);
                                                const expanded = expandedThemes.has(key);
                                                const questions = themeQuestions(theme);
                                                const selectedCount = selectedCountIn(questions);
                                                const allSelected = questions.length > 0 && selectedCount === questions.length;
                                                const someSelected = selectedCount > 0 && !allSelected;

                                                return (
                                                    <div className={'border rounded'} key={key}>
                                                        <div className={'d-flex align-items-center gap-2 px-3 py-2'}>
                                                            <input
                                                                ref={(el: HTMLInputElement | null) => {

                                                                    if (el) el.indeterminate = someSelected;

                                                                }}
                                                                checked={allSelected}
                                                                className={'form-check-input mt-0 flex-shrink-0'}
                                                                id={`theme-${key}`}
                                                                onChange={() => setThemeSelection(theme, !allSelected)}
                                                                type={'checkbox'}
                                                            />
                                                            <button
                                                                className={'btn btn-link text-decoration-none flex-fill d-flex justify-content-between align-items-center text-dark px-0'}
                                                                onClick={() => toggleExpanded(key)}
                                                                type={'button'}
                                                            >
                                                                <span className={'fw-semibold'}>
                                                                    {theme.Name}
                                                                </span>
                                                                <span className={'d-inline-flex align-items-center gap-2'}>
                                                                    <Badge bg={selectedCount > 0 ? 'primary' : 'secondary'}>
                                                                        {`${selectedCount}/${questions.length}`}
                                                                    </Badge>
                                                                    <i className={`fa-solid ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-muted`} />
                                                                </span>
                                                            </button>
                                                        </div>

                                                        {expanded && (
                                                            <div className={'border-top px-3 py-2 d-flex flex-column gap-3'}>
                                                                {theme.Objectives.map(objective => {

                                                                    const objQuestions = sortQuestions(objective.Questions ?? []);
                                                                    const objSelected = selectedCountIn(objQuestions);
                                                                    const objAll = objQuestions.length > 0 && objSelected === objQuestions.length;
                                                                    const objSome = objSelected > 0 && !objAll;

                                                                    return (
                                                                        <div className={`table-responsive${highlightObjectiveId === objective.ObjectiveId ? ' border border-primary rounded' : ''}`} id={`objective-block-${objective.ObjectiveId}`} key={objective.ObjectiveId}>
                                                                            <div className={'fw-semibold small mb-1'}>
                                                                                {objective.Name}
                                                                            </div>
                                                                            <Table borderless hover className={'mb-0 align-middle'} size={'sm'} style={{ minWidth: '46rem' }}>
                                                                                <thead>
                                                                                    <tr className={'small text-muted'}>
                                                                                        <th style={{ width: '2rem' }}>
                                                                                            <input
                                                                                                ref={(el: HTMLInputElement | null) => {

                                                                                                    if (el) el.indeterminate = objSome;

                                                                                                }}
                                                                                                checked={objAll}
                                                                                                className={'form-check-input mt-0'}
                                                                                                onChange={() => setObjectiveSelection(objective, !objAll)}
                                                                                                type={'checkbox'}
                                                                                            />
                                                                                        </th>
                                                                                        <th style={{ width: '5rem' }}>
                                                                                            {renderSortHeader('qid', formatMessage({ id: 'qid.58366cbe', defaultMessage: 'QID' }))}
                                                                                        </th>
                                                                                        <th style={{ minWidth: '14rem' }}>
                                                                                            {renderSortHeader('name', formatMessage({ id: 'question_name.b3f17db0', defaultMessage: 'Question Name' }))}
                                                                                        </th>
                                                                                        <th style={{ minWidth: '10rem' }}>
                                                                                            {renderSortHeader('type', formatMessage({ id: 'question_type.4b121765', defaultMessage: 'Question Type' }))}
                                                                                        </th>
                                                                                        <th style={{ minWidth: '10rem' }}>
                                                                                            {renderSortHeader('source', formatMessage({ id: 'source.f63dd3d7', defaultMessage: 'Source' }))}
                                                                                        </th>
                                                                                        <th className={'text-end'} style={{ width: '5rem' }}>
                                                                                            <FormattedMessage defaultMessage="Preview" id="preview.9fa9aebd" />
                                                                                        </th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {objQuestions.map(question => {

                                                                                        const sources = splitSourceString(question.Source ?? '');
                                                                                        const sourceDisplay = sources.length > 1 ? `${sources[0]} +${sources.length - 1}` : (sources[0] ?? '');

                                                                                        return (
                                                                                            <tr key={question.QuestionId}>
                                                                                                <td>
                                                                                                    <input
                                                                                                        checked={selectedQuestionIds.has(question.QuestionId)}
                                                                                                        className={'form-check-input mt-0'}
                                                                                                        onChange={() => toggleQuestion(question.QuestionId)}
                                                                                                        type={'checkbox'}
                                                                                                    />
                                                                                                </td>
                                                                                                <td className={'text-muted small'}>
                                                                                                    {question.QuestionId}
                                                                                                </td>
                                                                                                <td>
                                                                                                    {question.Name}
                                                                                                </td>
                                                                                                <td className={'text-muted small'}>
                                                                                                    {getQuestionTypeName(question) || '—'}
                                                                                                </td>
                                                                                                <td className={'small'}>
                                                                                                    {question.Source ?
                                                                                                        (
                                                                                                            <OverlayTrigger
                                                                                                                overlay={(
                                                                                                                    <Tooltip>
                                                                                                                        <FormattedMessage defaultMessage="From: {source}" id="from_source.86656754" values={{ source: question.Source }} />
                                                                                                                    </Tooltip>
                                                                                                                )}
                                                                                                                placement={'top'}
                                                                                                            >
                                                                                                                <span className={'text-primary text-decoration-underline'}>
                                                                                                                    {sourceDisplay}
                                                                                                                </span>
                                                                                                            </OverlayTrigger>
                                                                                                        )
                                                                                                        :
                                                                                                        (
                                                                                                            <span className={'text-muted'}>
                                                                                                                —
                                                                                                            </span>
                                                                                                        )}
                                                                                                </td>
                                                                                                <td className={'text-end'}>
                                                                                                    <button
                                                                                                        aria-label={formatMessage({ id: 'preview.9fa9aebd', defaultMessage: 'Preview' })}
                                                                                                        className={'btn btn-link text-decoration-none p-0 text-primary'}
                                                                                                        onClick={() => openQuestionPreview(objQuestions, question, objective.Name, theme.Name)}
                                                                                                        title={formatMessage({ id: 'preview.9fa9aebd', defaultMessage: 'Preview' })}
                                                                                                        type={'button'}
                                                                                                    >
                                                                                                        <i className={'fa-regular fa-eye'} />
                                                                                                    </button>
                                                                                                </td>
                                                                                            </tr>
                                                                                        );

                                                                                    })}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    );

                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );

                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                </Col>

            </Row>

            <QuestionPreviewModal
                includeControl={{
                    isIncluded: previewQuestion => selectedQuestionIds.has(previewQuestion.QuestionId),
                    onToggle: previewQuestion => toggleQuestion(previewQuestion.QuestionId)
                }}
                cultureId={cultureId}
                index={previewIndex}
                onHide={() => setState({ previewShow: false })}
                onNavigate={nextIndex => setState({ previewIndex: nextIndex })}
                questions={previewQuestions}
                show={previewShow}
                studentId={previewStudentId}
                teacherId={userId}
            />

        </div>
    );

};
